/**
 * Jitsi engine and conference configuration.
 *
 * Every value here was checked against the shipped bundle
 * (`dist/umd/lib-jitsi-meet.min.js`, v2180) rather than against the docs, because
 * the library's behaviour has moved on from what the architecture was written
 * against. Where the two disagree, the bundle wins and the difference is noted.
 */

/** Audio constraints that survive lib-jitsi-meet's whitelist. */
export interface AudioProcessingConstraints {
  autoGainControl?: boolean;
  channelCount?: 1 | 2;
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
}

export interface JitsiInitOptions {
  disableAudioLevels: boolean;
  audioQuality?: AudioQualityOptions;
}

export interface AudioQualityOptions {
  stereo?: boolean;
  enableOpusDtx?: boolean;
  opusMaxAverageBitrate?: number;
  opusMaxAverageBitrateMono?: number;
  opusMaxAverageBitrateStereo?: number;
}

/**
 * Target bitrate for the call.
 *
 * The default is roughly 32 kbps mono VoIP, which is tuned for speech and
 * hostile to a signal whose diagnostic content is 20–150 Hz. 128 kbps buys the
 * low end the headroom it needs (D18).
 */
export const AUSCULTATION_BITRATE = 128_000;

/**
 * Engine options for `JitsiMeetJS.init()`.
 *
 * Deliberately does NOT set `disableAP` / `disableAEC` / `disableNS` /
 * `disableAGC`. Those are engine-global: setting them for the stethoscope's
 * benefit would also strip echo cancellation from the doctor's voice, which is
 * being played into a TV speakerphone in the same room as the microphone (D16).
 *
 * The stethoscope does not need them anyway — it originates from a
 * `MediaStreamAudioDestinationNode`, never from `getUserMedia`, so Chromium's
 * capture DSP is not in its path at all.
 *
 * `disableHPF` is absent because the shipped bundle does not read it — grepping
 * v2180 finds zero occurrences. This is worth knowing rather than hoping: it
 * means the capture-path high-pass cannot be turned off, which independently
 * justifies keeping the stethoscope off the capture path entirely.
 */
export function buildInitOptions(): JitsiInitOptions {
  return {
    // Audio levels drive the speaking indicator and cost little.
    disableAudioLevels: false,
    audioQuality: buildAudioQuality(),
  };
}

/**
 * Opus configuration (D17, D18).
 *
 * `stereo: true` is not about channel count. Chromium's Opus encoder selects
 * `OPUS_APPLICATION_AUDIO` (music) for stereo and `OPUS_APPLICATION_VOIP` for
 * mono, and VoIP mode applies speech-model assumptions that discard exactly the
 * low-frequency content auscultation depends on. The flag is the only lever
 * Jitsi's config surface offers onto that switch.
 *
 * All three bitrate keys are set on purpose. v2180's
 * `TraceablePeerConnection.addTrackToPc()` recomputes the Opus parameters from
 * the track's real `channelCount` and reads `opusMaxAverageBitrateStereo` /
 * `opusMaxAverageBitrateMono` in preference to the flat `opusMaxAverageBitrate`
 * the architecture names. Setting only the flat key would leave the rate
 * unset on the path the library actually takes.
 */
export function buildAudioQuality(): AudioQualityOptions {
  return {
    stereo: true,
    // DTX gates the gaps between S1 and S2 as silence. The cost of leaving it
    // off is a little bandwidth during quiet; the cost of leaving it on is
    // heart sounds that never arrive.
    enableOpusDtx: false,
    opusMaxAverageBitrate: AUSCULTATION_BITRATE,
    opusMaxAverageBitrateMono: AUSCULTATION_BITRATE,
    opusMaxAverageBitrateStereo: AUSCULTATION_BITRATE,
  };
}

/**
 * Constraints for the call's microphone track (D16).
 *
 * Passing these explicitly matters for two reasons, both verified in the bundle.
 *
 * First, `RTCUtils.getConstraints()` only honours caller-supplied audio
 * constraints when `constraints.audio` is a non-boolean object; otherwise it
 * synthesises its own from the engine-global disable flags. Supplying an object
 * takes the branch we want.
 *
 * Second, that branch whitelists exactly four keys — `autoGainControl`,
 * `channelCount`, `echoCancellation`, `noiseSuppression` — and silently drops
 * everything else, `deviceId` included. Which is why the built-in microphone is
 * pinned through `micDeviceId` (D11) and never through the constraints object.
 *
 * `channelCount: 1` is explicit because with `stereo: true` set globally the
 * synthesised branch would otherwise force the microphone to 2 channels.
 */
export function buildMicConstraints(): AudioProcessingConstraints {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  };
}

export interface ConferenceOptions {
  openBridgeChannel: string;
  startSilent: boolean;
  audioQuality: AudioQualityOptions;
  /** Absent means lib-jitsi-meet's default, which is peer-to-peer on. */
  p2p?: { enabled: boolean };
  videoQuality?: {
    codecPreferenceOrder: string[];
    mobileCodecPreferenceOrder: string[];
  };
}

/**
 * VP8 first on every device, through the videobridge.
 *
 * Each sender encodes with the first codec in its own list that every other
 * participant announces. Left to lib-jitsi-meet's defaults a desktop leads with
 * AV1, and with the Android WebView patient that path delivered nothing: the
 * doctor sent ~2.3 Mbps of video to the bridge while the phone received 0 kbps
 * and never decoded a frame. VP8 is the one codec every browser, WebView and
 * videobridge version handles. Peer-to-peer calls keep the library defaults.
 */
const VIDEO_CODEC_ORDER = ['VP8', 'VP9', 'H264', 'AV1'];

/**
 * Diagnostics read from the page URL. Test-only: nothing links to them.
 *
 * `?p2p=0` keeps a two-person call on the videobridge. Two desktop browsers
 * normally connect peer-to-peer and never touch the bridge, so a fault on the
 * bridge path (the Android WebView case) can only be reproduced on desktop —
 * with full devtools and webrtc-internals — by turning peer-to-peer off.
 *
 * `?jitsiLog=info` raises lib-jitsi-meet from WARN, which shows its codec
 * selection and bridge-channel choice.
 *
 * `?codec=default` drops the VP8-first order and lets the library choose, to
 * compare against it.
 */
export function readJitsiDiagnostics(search = globalThis.location?.search ?? ''): {
  disableP2p: boolean;
  logLevel: 'WARN' | 'INFO';
  libraryCodecs: boolean;
} {
  const params = new URLSearchParams(search);
  return {
    disableP2p: params.get('p2p') === '0',
    logLevel: params.get('jitsiLog') === 'info' ? 'INFO' : 'WARN',
    libraryCodecs: params.get('codec') === 'default',
  };
}

export function buildConferenceOptions(search?: string): ConferenceOptions {
  const { disableP2p, libraryCodecs } = readJitsiDiagnostics(search);
  return {
    // The control plane (§6) rides this datachannel, so it must be open.
    openBridgeChannel: 'datachannel',
    startSilent: false,
    audioQuality: buildAudioQuality(),
    ...(disableP2p ? { p2p: { enabled: false } } : {}),
    ...(libraryCodecs
      ? {}
      : {
          videoQuality: {
            codecPreferenceOrder: VIDEO_CODEC_ORDER,
            mobileCodecPreferenceOrder: VIDEO_CODEC_ORDER,
          },
        }),
  };
}
